import React, { Component } from 'react';
import Quagga from '@ericblade/quagga2';
import jsQR from 'jsqr';
import WebcamComponent from 'react-webcam';

import Select from '#comp/Custom/Select';
import { Button } from 'flowbite-react';

import Context from '#context';
import { getMediaDevices, reportCameraError } from '#helper/camera';

function getMedian(arr) {
  const newArr = [...arr];
  newArr.sort((a, b) => a - b);
  const half = Math.floor(newArr.length / 2);
  if (newArr.length % 2 === 1) {
    return newArr[half];
  }
  return (newArr[half - 1] + newArr[half]) / 2;
}

function getMedianOfCodeErrors(decodedCodes) {
  const errors = decodedCodes.flatMap((x) => x.error);
  const medianOfErrors = getMedian(errors);
  return medianOfErrors;
}

const defaultConstraints = {
  width: 800,
  height: 600,
};

const QR_CHECK_INTERVAL = 100;
const MAX_QR_ERROR = 0.20;

const defaultLocatorSettings = {
  patchSize: 'medium',
  halfSample: true,
};

const defaultDecoders = ['ean_reader', 'code_128_reader'];

export class QRScanner extends Component {
  static contextType = Context;

  constructor(props) {
    super(props);
    this.scannerRef = React.createRef();
    this.state = {
      torchOn: false,
      zoom: 1,
    };
  }

  componentDidMount = async () => {
    try {
      await Quagga.init(
        {
          inputStream: {
            type: 'LiveStream',
            constraints: {
              ...defaultConstraints,
              ...(this.props.deviceid && {
                deviceId: this.props.deviceid,
              }),
              ...(!this.props.deviceid && {
                facingMode: 'environment',
              }),
            },
            target: this.scannerRef.current,
            willReadFrequently: true,
          },
          locator: defaultLocatorSettings,
          decoder: { readers: defaultDecoders },
          locate: true,
        },
        (err) => {
          if (err) {
            reportCameraError(this.context.t, err, 'QRScanner');
            return;
          }
          
          Quagga.start();
          
          // Make sure all canvas elements have the correct size
          // We use requestAnimationFrame to ensure the canvas elements are rendered
          requestAnimationFrame(() => {
            const container = this.scannerRef.current;
            if (!container) return;
            
            const containerRect = container.getBoundingClientRect();
            const canvasWidth = Math.min(defaultConstraints.width, Math.floor(containerRect.width));
            const canvasHeight = Math.min(defaultConstraints.height, Math.floor(containerRect.height));
            
            const canvases = container.querySelectorAll('canvas');
            canvases.forEach((canvas) => {
              // Set the internal dimensions based on the container size
              canvas.width = canvasWidth;
              canvas.height = canvasHeight;
              
              // CSS makes it responsive
              canvas.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                border: none;
                display: block;
              `;
            });
			window.scrollTo({ top: document.body.scrollHeight });
          });
        }
      );
    } catch {
    }

    // Register event listeners
    Quagga.onDetected(this.handleDetected);
    Quagga.onProcessed(this.handleProcessed);
  };

  handleDetected = (result) => {
    try {
      if (!result || !result.codeResult) return;

      // Error-Check: only accept if Quagga is at least MAX_QR_ERROR sure
      const err = getMedianOfCodeErrors(
        result.codeResult.decodedCodes
      );
      if (err > 0 && err < MAX_QR_ERROR) {
        const barcode = result.codeResult.code;
        this.props.resultScanner(barcode);
      }
    } catch {
      // Error handling
    }
  };

  handleProcessed = (result) => {
    if (!result) return;

    const now = Date.now();

    // Try to scan the QR-Code
    if (!this.lastQRAttempt) this.lastQRAttempt = 0;
    
    if (now - this.lastQRAttempt > QR_CHECK_INTERVAL) {
      this.lastQRAttempt = now;
      
      try {
        // Use Quagga's internal Canvas or Video
        const canvas = Quagga.canvas.dom.image || 
                      Quagga.canvas.dom.video || 
                      document.querySelector('video');
        
        if (!canvas) {
          return;
        }

        // Create a temporary Canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = defaultConstraints.width;
        tempCanvas.height = defaultConstraints.height;
        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
        
        // Draw the Video/Canvas on the temp Canvas
        if (canvas instanceof HTMLVideoElement) {
          tempCtx.drawImage(canvas, 0, 0, defaultConstraints.width, defaultConstraints.height);
        } else if (canvas instanceof HTMLCanvasElement) {
          tempCtx.drawImage(canvas, 0, 0, defaultConstraints.width, defaultConstraints.height);
        }
        
        // Get the Image Data
        const imageData = tempCtx.getImageData(0, 0, defaultConstraints.width, defaultConstraints.height);
        
        if (!imageData || !imageData.data) {
          return;
        }
        
        // Try to recognize the QR-Code
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (qrCode && qrCode.data) {
          this.props.resultScanner(qrCode.data);
          return;
        }
      } catch {
        // No Error
      }
    }
  };

  enableTorch = async () => {
    try {
      await Quagga.CameraAccess.enableTorch();
      this.setState({ torchOn: true });
    } catch {
		// No Error
    }
  };

  disableTorch = async () => {
    try {
      await Quagga.CameraAccess.disableTorch();
      this.setState({ torchOn: false });
    } catch {
		// No Error
    }
  };

  toggleTorch = async () => {
    if (this.state.torchOn) {
      await this.disableTorch();
    } else {
      await this.enableTorch();
    }
  };

  setZoom = async (zoomLevel) => {
    try {
      const track = Quagga.CameraAccess.getActiveTrack();
      if (track) {
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          await track.applyConstraints({
            advanced: [{ zoom: zoomLevel }],
          });
          this.setState({ zoom: zoomLevel });
        }
      }
    } catch {
		// No Error
    }
  };

  componentWillUnmount = () => {
    Quagga.stop();
  };

  render() {
    const { torchOn, zoom } = this.state;
	const { t } = this.context;

    return (
      <div className="flex flex-col gap-4">

		<div className="flex gap-2 justify-center">
		<Button
			onClick={this.toggleTorch}
			color={torchOn ? 'warning' : 'gray'}
		>
			{torchOn ? t('cam.startTorch') : t('cam.stopTorch')}
		</Button>

		<div className="flex items-center gap-2">
			<label htmlFor="zoom-slider">{t('cam.zoom')}:</label>
			<input
				id="zoom-slider"
				type="range"
				min="1"
				max="5"
				step="0.5"
				value={zoom}
				onChange={(e) => this.setZoom(parseFloat(e.target.value))}
				className="w-32"
			/>
			<span>{zoom.toFixed(1)}x</span>
		</div>
		</div>

        <div className="flex justify-center">
          <div
            ref={this.scannerRef}
            className="rounded-lg relative shadow-lg overflow-hidden w-full"
          />
        </div>
      </div>
    );
  }
}

export class Selector extends Component {
  static contextType = Context;

  state = {
    videoDevices: [],
  };

  componentDidMount = async () => {
    const { t } = this.context;
    let cameras = [];
    try {
      // Camera access first, otherwise the list has no labels (front/back).
      await Quagga.CameraAccess.request(null, {});
      await Quagga.CameraAccess.release();
      cameras = await Quagga.CameraAccess.enumerateVideoDevices();
    } catch (error) {
      // Without this the list stays empty and the user sees no reason.
      reportCameraError(t, error, 'Selector');
      return;
    }

    if (cameras.length > 0) {
      this.setState({ videoDevices: cameras });
    } else {
      const deviceInfo = await getMediaDevices(t);
      this.setState({ videoDevices: deviceInfo });
    }
  };

  render() {
    const { selectedDeviceId, startCam, startScanner, changeSelected } =
      this.props;
    const { videoDevices } = this.state;
    const { t } = this.context;

    return (
      <div className="flex gap-2 xs:flex-row flex-col mb-2">
        <Select
          defaultValue={selectedDeviceId || 'undefined'}
          onChange={(e) => changeSelected(e.target.value)}
          name="selectCamera"
          label={t('cam.labelCamSelector')}
          className="grow"
        >
          <option value={'undefined'}>
            {t('cam.undefinedCamSelector')}
          </option>
          {videoDevices.map((device) => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label || device.deviceId}
            </option>
          ))}
        </Select>
        <div className="flex items-center flex-col xs:flex-row gap-2">
          <Button
            className="w-full xs:w-auto"
            disabled={selectedDeviceId === 'undefined'}
            onClick={startScanner.bind(this)}
          >
            {startCam ? t('cam.stopCam') : t('cam.startCam')}
          </Button>
        </div>
      </div>
    );
  }
}

export default class Webcam extends Component {
	constructor(props) {
		super(props)
		this.webcamRef = React.createRef()
	}

	setRef = (webcam) => {
		this.webcamRef = webcam
	}

	captureImage = () => {
		const imageSrc = this.webcamRef.getScreenshot()

		this.props.tookImage(imageSrc)
	}

	handleVideoLoaded = () => {
		window.scrollTo({ top: document.body.scrollHeight })
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.takePicture !== this.props.takePicture &&
			this.props.takePicture === true
		) {
			this.captureImage()
		}
	}

	render() {
		return (
			<div className="flex justify-center">
				<WebcamComponent
					className="rounded-lg"
					videoConstraints={{ deviceId: this.props.deviceId }}
					muted
					width={800}
					height={600}
					audio={false}
					ref={this.setRef}
					screenshotFormat="image/jpeg"
					onLoadedMetadata={this.handleVideoLoaded}
				/>
			</div>
		)
	}
}